"use client";

import { localLogout } from "@/lib/auth-client";
import { http } from "@/lib/http";
import useDataStore from "@/stores/data-store";
import useSessionStore from "@/stores/session-store";
import { IEmployee } from "@/features/organization/employees";
import { Session } from "next-auth";
import { SessionProvider as NextSessionProvider } from "next-auth/react";
import { ReactNode, useEffect } from "react";
import useSWR from "swr";

const endpoint = "v1/payroll/employees/current";
const userConfigsEndpoint = "v1/payroll/user-configs/current-user";

type UserConfig = {
  key: string;
  value: string;
};

const fetcher = async () => {
  const response = await http.get<IEmployee>(endpoint, {
    baseURL: process.env.NEXT_PUBLIC_API_ENDPOINT,
  });
  return response.data;
};

const fetchUserConfigs = async () => {
  const response = await http.get<UserConfig[]>(userConfigsEndpoint, {
    baseURL: process.env.NEXT_PUBLIC_API_ENDPOINT,
  });
  return response.data;
};

const SessionProvider = ({
  children,
  session,
}: {
  children: ReactNode;
  session: Session | null;
}) => {
  const { setAccessToken, setUsername, setSessionError } = useSessionStore();
  const {
    setCurrentEmployee,
    setUserConfigs,
    setHasLoadedUserConfigs,
  } = useDataStore();

  const accessToken = session?.accessToken as string | undefined;
  const username = session?.user?.name ?? undefined;
  const sessionError = (session as any)?.error as
    | "RefreshAccessTokenError"
    | "RefreshAccessTokenTransientError"
    | undefined;
  const { data: employee, isLoading: isEmployeeLoading } = useSWR(
    accessToken ? endpoint : null,
    fetcher
  );
  const { data: userConfigs, isLoading: isUserConfigsLoading } = useSWR(
    accessToken ? userConfigsEndpoint : null,
    fetchUserConfigs
  );

  useEffect(() => {
    if (!isEmployeeLoading && employee) {
      setCurrentEmployee(employee);
    }
  }, [employee, isEmployeeLoading, setCurrentEmployee]);

  useEffect(() => {
    setAccessToken(accessToken);
    setUsername(username);
    setSessionError(sessionError);
  }, [accessToken, sessionError, setAccessToken, setSessionError, setUsername, username]);

  useEffect(() => {
    if (sessionError === "RefreshAccessTokenError") {
      void localLogout();
    }
  }, [sessionError]);

  useEffect(() => {
    if (isUserConfigsLoading) {
      return;
    }

    setUserConfigs(
      Object.fromEntries((userConfigs ?? []).map((config) => [config.key, config.value]))
    );
    setHasLoadedUserConfigs(true);
  }, [
    isUserConfigsLoading,
    setHasLoadedUserConfigs,
    setUserConfigs,
    userConfigs,
  ]);

  return (
    <NextSessionProvider session={session} refetchInterval={5 * 60}>
      {children}
    </NextSessionProvider>
  );
};

export default SessionProvider;
